"use client";

import { Button } from "@/components/ui/Button";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

export default function PromotionalBanner({
  title,
  description,
  from,
  to,
  className = "",
  date,
  duration,
  price,
  buttonText,
  icon,
  type = "flight", // flight, bus, ferry, hotel, ...
  url,
  onButtonClick,
  onAnalyticsEvent,
  data = null, // Optional: generic data { from, to, date, duration, price, buttonText, url, type, icon, title }
  originCode,
  destinationCode,
}) {
  // Choose i18n namespace by type
  const ns = ["bus", "flight", "ferry", "hotel"].includes(type)
    ? type
    : "common";
  const t = useTranslationDetector(ns);

  // Generic adapter (no domain mapping): prefer explicit props, fallback to data's same-field names
  const effective = {
    from: from ?? data?.from,
    to: to ?? data?.to,
    date: date ?? data?.date,
    duration: duration ?? data?.duration,
    price: price ?? data?.price,
    buttonText:
      buttonText ??
      data?.buttonText ??
      t("goToDetails", { default: "Detaya Git" }),
    url: url ?? data?.url,
    type: type ?? data?.type ?? "flight",
    icon: icon ?? data?.icon,
    title: title ?? data?.title,
    originCode: originCode ?? data?.originCode,
    destinationCode: destinationCode ?? data?.destinationCode,
  };

  // Required fields after adaptation
  if (
    !effective.from ||
    !effective.to ||
    !effective.date ||
    !effective.duration ||
    !effective.price ||
    !effective.buttonText
  ) {
    return null;
  }

  // Type-specific config
  const getTypeConfig = (tp) => {
    switch (tp) {
      case "bus":
        return {
          icon: "new-bus",
          bgColor: "bg-background-info-default",
          buttonColor: "bg-background-info-default!",
        };
      case "flight":
        return {
          icon: "plane",
          bgColor: "bg-background-info-default",
          buttonColor: "bg-background-info-default!",
        };
      case "ferry":
        return {
          icon: "ship",
          bgColor: "bg-background-info-default",
          buttonColor: "bg-background-info-default!",
        };
      case "hotel":
        return {
          icon: "hotel",
          bgColor: "bg-background-info-default",
          buttonColor: "bg-background-info-default!",
        };
      default:
        return {
          icon: "new-bus",
          bgColor: "bg-background-info-default",
          buttonColor: "bg-background-info-default!",
        };
    }
  };

  const typeConfig = getTypeConfig(effective.type);
  const displayIcon = effective.icon || typeConfig.icon;
  const resolveIcon = (name) =>
    (name &&
      (byPrefixAndName.fak?.[name] ||
        byPrefixAndName.fas?.[name] ||
        byPrefixAndName.far?.[name])) ||
    null;
  const defaultTitles = {
    flight: "Uçuş Önerisi",
    bus: "Otobüs Önerisi",
    ferry: "Feribot Önerisi",
    hotel: "Otel Önerisi",
    default: "Öneri",
  };
  const displayTitle =
    effective.title ||
    t("promotionalBannerTitle", {
      default: defaultTitles[effective.type] || defaultTitles.default,
    });

  return (
    <div
      className={`border-border-info-default relative flex flex-row flex-wrap items-end md:items-center justify-between rounded-lg border bg-white pl-5 md:pl-8 pr-4 md:pr-6 pt-8 pb-4 h-[132px] md:h-[122px] ${className}`}
    >
      {/* badge */}
      <div
        className={`${typeConfig.bgColor} absolute top-0 left-0 flex items-center justify-center gap-[6px] rounded-tl-[8px] rounded-br-[8px] px-2 py-1.5 text-white`}
      >
        {displayIcon && resolveIcon(displayIcon) && (
          <FontAwesomeIcon icon={resolveIcon(displayIcon)} className="text-[20px] max-w-[20px]" />
        )}
        <span className="text-sm font-medium leading-19">{displayTitle}</span>
      </div>

      <div className="flex flex-col md:flex-row md:justify-between md:flex-2">


        <div className="flex gap-3 flex-row md:flex-1">
          <div className="flex h-full flex-col items-center justify-center py-3.5">
            <div className="bg-foreground h-2 w-2 rounded-full"></div>
            <div className="bg-foreground h-[22px] md:h-[30px] w-[1px]"></div>
            <div className="border-border-foreground h-2 w-2 rounded-full border-2 bg-white"></div>
          </div>
          <div className="flex flex-col justify-between py-2.5">
            <span className="text-foreground text-sm font-semibold leading-19">
              {effective.from}
            </span>
            <span className="text-foreground text-sm font-semibold leading-19">
              {effective.to}
            </span>
          </div>
        </div>


        <div className="flex md:items-center md:justify-center md:flex-1">
          
          <div className="flex md:flex-col items-start justify-center gap-4">
            <div className="flex flex-row items-center justify-center gap-1.5">
              <FontAwesomeIcon icon={byPrefixAndName.far["calendar"]} className="text-foreground-muted md:text-foreground text-[16px] max-w-[16px]" />{" "}
              <span className="text-foreground-muted md:text-foreground text-sm font-medium leading-19">
                {effective.date}
              </span>
            </div>
            <div className="flex flex-row items-center justify-center gap-1.5">
              <FontAwesomeIcon icon={byPrefixAndName.far["clock"]} className="text-foreground-muted md:text-foreground text-[16px] max-w-[16px]" />{" "}
              <span className="text-foreground-muted md:text-foreground text-sm font-medium leading-19">
                {effective.duration}
              </span>
            </div>
          </div>

          
        </div>



      </div>


      <div className="flex md:flex-1 md:justify-end">

        <div className="flex flex-col justify-center items-center gap-2">

          <span className="text-foreground text-base-custom font-semibold leading-[26px] md:leading-[30px]">
            {effective.price}
          </span>

          <Button
            className={`${typeConfig.buttonColor} text-[13px]! px-0! leading-[17px]! w-full min-w-[92px] md:min-w-[107px] h-[36px]`}
            onClick={() => {
              onAnalyticsEvent?.(effective);
              if (onButtonClick) return onButtonClick();
              if (typeof window !== "undefined" && effective.url) {
                setTimeout(() => {
                  window.location.href = effective.url;
                }, 222);
              }
            }}
          >
            {effective.buttonText}
          </Button>
        </div>


      </div>



    </div>
  );
}
